import {useCallback} from 'react'
import {useTheme} from 'styled-components'
import {useController} from 'react-hook-form'
import CreatableSelect from 'react-select/creatable'
import ReactSelect, {components} from 'react-select'
import AsyncSelect from 'react-select/async'
import PropTypes from 'prop-types'

import {CloseIcon} from '../../theme/icons'
import {ErrorMessage} from '../error-message/ErrorMessage'

import {reactSelectsStyleGenerator, StyledSelect, StyledLabel} from './style'

export const Select = ({
    className,
    control,
    name,
    options = [],
    label,
    placeholder,
    helpText,
    defaultValue,
    isSearchable = true,
    isClearable = false,
    isCreatable = false,
    addOptionMessage,
    maxItems = 100,
    isMulti,
    onChangeCallback,
    closeMenuOnSelect = !isMulti,
    customComponents,
    menuPlacement = 'auto',
    menuPosition = 'absolute',
    disabled,
    isAsync,
    loadOptions,
    ...rest
}) => {
    const {
        field: {onChange, ...fieldProps},
        fieldState: {error, isTouched},
        formState
    } = useController({
        name,
        control,
        defaultValue
    })

    // This extends the default onChange
    const onChangeHandler = value => {
        if (typeof onChangeCallback === 'function') onChangeCallback(value)
        if (!isMulti || (isMulti && value.length <= maxItems)) onChange(value) // Limit multi select
    }

    // Label for new item creation
    const createLabel = useCallback(
        value => (
            <span style={{fontSize: 14}}>
                {addOptionMessage}
                <span>{value}</span>
            </span>
        ),
        [addOptionMessage]
    )

    const theme = useTheme()

    const reactSelectsStyle = reactSelectsStyleGenerator(
        theme,
        isMulti,
        isCreatable,
        isAsync,
        error,
        isTouched,
        disabled,
        rest.singleValueCustomStyle,
        rest.optionCustomStyle,
        rest.menuCustomStyle,
        rest.multiValueLabelCustomStyle,
        rest.multiValueCustomStyle,
        rest.controlCustomStyle,
        rest.menuContainerCustomStyle,
        rest.placeholderStyle,
        menuPosition
    )

    const selectProps = {
        options,
        closeMenuOnSelect,
        isSearchable,
        isClearable,
        isMulti,
        isDisabled: rest.readOnly || disabled,
        classNamePrefix: isCreatable ? 'creatable_select' : 'select',
        styles: reactSelectsStyle,
        placeholder,
        menuPosition,
        menuPlacement,
        ...rest
    }

    const components = {
        MultiValueRemove,
        ...customComponents
    }

    return (
        <StyledSelect className={className}>
            {!!label && <StyledLabel>{label}</StyledLabel>}
            {isAsync ? (
                <AsyncSelect
                    onChange={onChangeHandler}
                    loadOptions={loadOptions}
                    {...selectProps}
                    components={components}
                    {...formState}
                    {...fieldProps}
                    {...selectProps}
                />
            ) : isCreatable ? (
                <CreatableSelect
                    formatCreateLabel={createLabel}
                    onChange={onChangeHandler}
                    components={components}
                    {...formState} // from Controller
                    {...fieldProps} // from Controller
                    {...selectProps} // from Component
                />
            ) : (
                <ReactSelect
                    onChange={onChangeHandler}
                    {...selectProps}
                    menuPosition={menuPosition}
                    components={components}
                    {...formState}
                    {...fieldProps}
                />
            )}
            {!!error?.message && <ErrorMessage message={error.message} />}
            {!!helpText && <small>{helpText}</small>}
        </StyledSelect>
    )
}

const MultiValueRemove = props => {
    const theme = useTheme()

    return (
        <components.MultiValueRemove {...props}>
            <CloseIcon fill={theme.palette.neutral.black} width={20} height={20} />
        </components.MultiValueRemove>
    )
}

Select.propTypes = {
    className: PropTypes.string,
    control: PropTypes.object,
    name: PropTypes.string,
    options: PropTypes.array,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    isSearchable: PropTypes.bool,
    isClearable: PropTypes.bool,
    isCreatable: PropTypes.bool,
    addOptionMessage: PropTypes.bool,
    maxItems: PropTypes.number,
    isMulti: PropTypes.bool,
    onChangeCallback: PropTypes.func,
    closeMenuOnSelect: PropTypes.bool,
    customComponents: PropTypes.object,
    isAsync: PropTypes.bool,
    disabled: PropTypes.bool,
    loadOptions: PropTypes.func,
    menuPlacement: PropTypes.string,
    menuPosition: PropTypes.string
}
